<template>
  <!-- Somente visível em telas menores (mobile) -->
  <div class="d-lg-none">
    
    <!-- Submenu Horizontal (Aparece apenas quando logado) -->
    <transition name="slide-up">
      <div 
        v-if="auth.isAuthenticated"
        class="app-submenu-scroller"
        :class="themeStore.isDark ? 'dark-bg' : 'light-bg'"
      >
        <nav class="d-flex align-items-center">
          <router-link to="/dashboard" class="submenu-link d-flex align-items-center" style="gap: 8px;">
            <template v-if="auth.profilePictureUrl">
              <img :src="auth.profilePictureUrl" alt="Avatar" style="width: 24px; height: 24px; border-radius: 50%; object-fit: cover; border: 1px solid rgba(128, 128, 128, 0.25);">
            </template>
            <template v-else>
              <i class="uil uil-apps"></i>
            </template>
            <span>{{ auth.nickname || 'Dashboard' }}</span>
          </router-link>
          <router-link v-if="auth.isConsultor" to="/painel" class="submenu-link">Painel Consultor</router-link>
          <router-link v-if="auth.isAuditor" to="/admin/cursos" class="submenu-link">Gestão Cursos</router-link>
          <router-link v-if="auth.isAuditor" to="/admin/artigos" class="submenu-link">Redação</router-link>
          <router-link to="/mailbox" class="submenu-link">E-mail</router-link>
          <router-link v-if="auth.isSuperAdmin" to="/superadmin" class="submenu-link text-danger">Modo Deus</router-link>
        </nav>
      </div>
    </transition>

    <!-- Menu Inferior Principal -->
    <nav class="app-menu" :class="{ 'dark': themeStore.isDark }">
      <ul class="app-menu-container">
        <li class="app-menu-item" :class="{ current: $route.path === '/campeonato' || $route.path === '/regulamento' }">
          <router-link to="/campeonato" class="app-menu-link" title="Campeonato Off Z Wagon">
            <i class="bi-trophy"></i>
            <span class="visually-hidden">Campeonato</span>
          </router-link>
        </li>
        <li class="app-menu-item" :class="{ current: $route.path === '/magazine' }">
          <router-link to="/magazine" class="app-menu-link" title="Magazine" aria-label="Magazine">
            <i class="bi-newspaper" aria-hidden="true"></i>
            <span class="visually-hidden">Magazine</span>
          </router-link>
        </li>
        <li class="app-menu-item" :class="{ current: $route.path.startsWith('/cursos') }">
          <router-link to="/cursos" class="app-menu-link" title="Cursos" aria-label="Cursos">
            <i class="bi-journal-check"></i>
            <span class="visually-hidden">Cursos</span>
          </router-link>
        </li>
        <li class="app-menu-item" :class="{ current: $route.path.startsWith('/masterclass') }">
          <router-link to="/masterclass" class="app-menu-link" title="Masterclass" aria-label="Masterclass">
            <i class="bi-award"></i>
            <span class="visually-hidden">Masterclass</span>
          </router-link>
        </li>
        <!-- Alternar Tema Claro / Escuro -->
        <li class="app-menu-item">
          <a 
            href="#" 
            @click.prevent="themeStore.toggleTheme()" 
            class="app-menu-link"
            :title="themeStore.isDark ? 'Modo Claro' : 'Modo Escuro'"
            :aria-label="themeStore.isDark ? 'Mudar para Modo Claro' : 'Mudar para Modo Escuro'"
          >
            <i :class="themeStore.isDark ? 'bi-sun-fill text-white' : 'bi-moon-stars'"></i>
            <span class="visually-hidden">{{ themeStore.isDark ? 'Modo Claro' : 'Modo Escuro' }}</span>
          </a>
        </li>
        <li class="app-menu-item">
          <!-- Login/Logout Toggle -->
          <a v-if="!auth.isAuthenticated" href="#" @click.prevent="showLoginModal = true" class="app-menu-link">
            <i class="bi-person-circle"></i>
            <span class="visually-hidden">Login</span>
          </a>
          <a v-else href="#" @click.prevent="auth.logout('/')" class="app-menu-link">
            <i class="bi-box-arrow-right text-danger"></i>
            <span class="visually-hidden">Logout</span>
          </a>
        </li>
      </ul>
    </nav>
    
    <!-- Modal de Login -->
    <LoginModal v-if="showLoginModal" @close="showLoginModal = false" />
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useAuthStore } from '../stores/auth'
import { useThemeStore } from '../stores/theme'
import LoginModal from './LoginModal.vue'

const auth = useAuthStore()
const themeStore = useThemeStore()
const showLoginModal = ref(false)
</script>

<style scoped>
/* =========================================
   Animação do Submenu (Slide Up)
   ========================================= */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: bottom;
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.slide-up-enter-to,
.slide-up-leave-from {
  opacity: 1;
  transform: translateY(0);
}

/* Oculte a scrollbar padrão para o menu horizontal mas permita rolagem suave */
.app-submenu-scroller {
  position: fixed;
  bottom: 60px; /* A altura exata do .app-menu no Canvas é 60px */
  left: 0;
  width: 100%;
  z-index: 298; /* Abaixo do .app-menu que tem 299 */
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  padding: 10px 15px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; /* Firefox */
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.app-submenu-scroller::-webkit-scrollbar {
  display: none; /* Chrome/Safari/Opera */
}

/* Light Theme - Alinhado com o Header de PC */
.app-submenu-scroller.light-bg {
  background-color: rgba(255, 255, 255, 0.65) !important;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.03);
}

/* Dark Theme - Alinhado com o Header de PC */
.app-submenu-scroller.dark-bg {
  background-color: rgba(0, 0, 0, 0.65) !important;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.3);
}

.submenu-link {
  display: inline-block;
  padding: 5px 15px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  text-decoration: none;
  color: inherit;
  transition: color 0.2s;
}

/* Inativo legível pela cor (antes: opacity 0.65, ~3:1); ativo/hover com cor cheia */
.dark-bg .submenu-link {
  color: rgba(255, 255, 255, 0.72);
}

.dark-bg .submenu-link:hover, .dark-bg .submenu-link.router-link-active {
  color: #ffffff;
}

.light-bg .submenu-link {
  color: rgba(33, 37, 41, 0.75);
}

.light-bg .submenu-link:hover, .light-bg .submenu-link.router-link-active {
  color: #000000;
}

/* Overrides para o Menu Inferior Principal Mobile (.app-menu) para alinhar com o Header PC */
:deep(.app-menu),
.app-menu {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.app-menu:not(.dark) {
  background-color: rgba(255, 255, 255, 0.75) !important;
  backdrop-filter: blur(12px) !important;
  -webkit-backdrop-filter: blur(12px) !important;
  border-top: 1px solid rgba(0, 0, 0, 0.05) !important;
}

.app-menu.dark {
  background-color: rgba(0, 0, 0, 0.75) !important;
  backdrop-filter: blur(12px) !important;
  -webkit-backdrop-filter: blur(12px) !important;
  border-top: 1px solid rgba(255, 255, 255, 0.05) !important;
}

</style>
